import {
  type CSSProperties,
  type TransitionEvent,
  useCallback,
  useMemo,
} from "react";
import { useTranslation } from "../../i18n";
import { cn } from "../../utils";
import { usePanelContext } from "./context";
import { PanelHeader } from "./PanelHeader";
import { PanelResizeHandle } from "./PanelResizeHandle";
import type { PanelSide } from "./types";

export const PanelContent = () => {
  const {
    title,
    description,
    children,
    side = "right",
    className,
    handleAnimationEnd,
    visible,
    enterDuration = 200,
    exitDuration = 150,
    titleId,
    descriptionId,
    currentWidth,
    resizable = true,
    isMobile,
  } = usePanelContext();

  const { rtl } = useTranslation();

  const positionStyles = {
    right: "inset-y-0 end-0",
    left: "inset-y-0 start-0",
  };

  const onTransitionEnd = useCallback(
    (event: TransitionEvent<HTMLDivElement>): void => {
      if (event.target === event.currentTarget) {
        handleAnimationEnd();
      }
    },
    [handleAnimationEnd],
  );

  const getTransformValue = (
    panelSide: PanelSide,
    isVisible: boolean,
  ): string => {
    if (isVisible) return "translate(0, 0)";
    const transforms: Record<PanelSide, string> = {
      right: rtl ? "translateX(-100%)" : "translateX(100%)",
      left: rtl ? "translateX(100%)" : "translateX(-100%)",
    };
    return transforms[panelSide];
  };

  const transitionStyle = useMemo((): CSSProperties => {
    return {
      transitionProperty: "transform",
      transitionDuration: `${visible ? enterDuration : exitDuration}ms`,
      transitionTimingFunction: visible ? "ease-out" : "ease-in",
      transform: getTransformValue(side, visible),
      width: isMobile ? "100%" : currentWidth,
    };
  }, [visible, enterDuration, exitDuration, side, rtl, isMobile, currentWidth]);

  const getBorderStyles = () => {
    if (side === "left") {
      return "border-e border-neutral-500/10";
    }
    return "border-s border-neutral-500/10";
  };

  const baseStyles = cn(
    "fixed z-[70] bg-white shadow-xl flex flex-col inset-y-0",
    getBorderStyles(),
  );

  return (
    <div
      role="complementary"
      aria-label={typeof title === "string" ? title : undefined}
      aria-labelledby={title && typeof title !== "string" ? titleId : undefined}
      aria-describedby={description ? descriptionId : undefined}
      data-state={visible ? "open" : "closed"}
      data-side={side}
      className={cn(baseStyles, positionStyles[side], className)}
      style={transitionStyle}
      onTransitionEnd={onTransitionEnd}
    >
      {resizable && !isMobile && <PanelResizeHandle />}
      {(title || description) && <PanelHeader />}
      {children}
    </div>
  );
};
